import type { OdbNode } from "../../api/types";
import type { Path } from "./tree";

/** Numérotation des unités construites (amplificateurs APP-6 du symbole) : en bas à gauche la désignation unique
 *  (T, numéro de l'unité), en bas à droite la formation supérieure (M, numéro de l'unité N+1). Chiffres seulement,
 *  quatre au plus. M suit par défaut le T de la formation : rien n'est recopié, changer le T d'une unité change à
 *  l'affichage le M de tous ses subordonnés qui ne l'imposent pas. */

export const NUMBER_RE = /^[0-9]{1,4}$/;

/** Numéro saisi, espaces retirés ; vide : absent. */
export function cleanNumber(value: string | null | undefined): string | null {
  const text = (value ?? "").replace(/\s+/g, "");
  return text ? text : null;
}

/** Numéro valide (ou absent). */
export function validNumber(value: string | null | undefined): boolean {
  const text = cleanNumber(value);
  return text === null || NUMBER_RE.test(text);
}

/** Numéro à dessiner sur un symbole : seulement s'il est valide (« 1st », « 2e » ne s'impriment jamais, comme sur la
 *  carte), sinon rien. */
export function drawableNumber(value: string | null | undefined): string | null {
  const text = cleanNumber(value);
  return text !== null && NUMBER_RE.test(text) ? text : null;
}

/** Chiffres en tête d'un nom (« 92e RI » → « 92 », « 3 DIV » → « 3 »), ou null. */
export function leadingDigits(name: string | null | undefined): string | null {
  const found = /^\s*(\d{1,4})(?!\d)/.exec(name ?? "");
  return found ? (found[1] ?? null) : null;
}

/** Formation supérieure affichée (M) d'une unité : celle qu'elle impose ; la racine, celle saisie ; sinon le T de sa
 *  formation (`auto`). */
export function effectiveHigher(tree: OdbNode, path: Path): { value: string | null; auto: boolean } {
  let node = tree;
  let parent: OdbNode | null = null;
  for (const i of path) {
    parent = node;
    node = node.children[i] as OdbNode;
  }
  const own = cleanNumber(node.higher);
  if (own) return { value: own, auto: false };
  if (!parent) return { value: null, auto: false };
  return { value: cleanNumber(parent.number), auto: true };
}

/** Prochain T libre parmi des unités sœurs de même échelon : le plus grand numéro + 1, à partir de 1. */
export function nextFreeNumber(siblings: readonly OdbNode[], echelon: string | null | undefined): string {
  const used = siblings
    .filter((s) => (s.echelon ?? null) === (echelon ?? null))
    .map((s) => Number(cleanNumber(s.number)))
    .filter((n) => Number.isInteger(n) && n > 0);
  return String(used.length ? Math.max(...used) + 1 : 1);
}

/** Subordonnés renumérotés 1, 2, 3… dans leur ordre, échelon par échelon (« Numéroter 1, 2, 3… »). */
export function renumbered(children: readonly OdbNode[]): OdbNode[] {
  const counters = new Map<string, number>();
  return children.map((child) => {
    const key = child.echelon ?? "";
    const next = (counters.get(key) ?? 0) + 1;
    counters.set(key, next);
    return { ...child, number: String(next) };
  });
}

/** Numéros portés par plusieurs sœurs de même échelon (avertissement du serveur, rappelé sur la ligne). */
export function duplicateNumbers(children: readonly OdbNode[]): Set<number> {
  const seen = new Map<string, number>();
  const duplicates = new Set<number>();
  children.forEach((child, index) => {
    const number = cleanNumber(child.number);
    if (!number) return;
    const key = `${child.echelon ?? ""}|${number}`;
    const first = seen.get(key);
    if (first !== undefined) {
      duplicates.add(first);
      duplicates.add(index);
    } else seen.set(key, index);
  });
  return duplicates;
}
